import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Empty, Skeletons, SectionHead } from "@/components/Primitives";
import { toast } from "sonner";
import { BarChart3, ListChecks, FileAudio, Timer } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#1D4ED8", "#06B6D4", "#60A5FA", "#10B981", "#6366F1"];

export default function Reports() {
  const [d, setD] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports/overview").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
    api.get("/audit-logs", { params: { limit: 25 } }).then(({ data }) => setLogs(data)).catch(() => {});
  }, []);

  if (loading) return <Skeletons rows={3} className="h-40" />;
  if (!d) return <Empty icon={BarChart3} title="Data laporan tidak tersedia" sub="Gagal memuat data." />;

  const completion = [
    { name: "Selesai", value: d.completion.done },
    { name: "Berjalan", value: d.completion.in_progress },
    { name: "Open", value: d.completion.open },
  ].filter((x) => x.value > 0);

  return (
    <div className="space-y-8" data-testid="reports-page">
      <div>
        <p className="label-xs">Analitik</p>
        <h1 className="mt-2.5 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Reports</h1>
        <p className="mt-2 text-sm text-slate-500">Kinerja rapat, distribusi divisi, dan penyelesaian action items.</p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {[["Total Rapat", d.meetings_total, FileAudio], ["Total Action Items", d.action_items_total, ListChecks],
          ["Action Selesai", d.completion.done, Timer], ["Divisi Terlibat", d.by_division.length, BarChart3]].map(([l, v, Icon], i) => (
          <div key={l} className="clay p-6 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`report-stat-${i}`}>
            <div className="flex items-center gap-2 text-slate-400"><Icon size={14} /><span className="label-xs">{l}</span></div>
            <p className="mt-3 font-display text-3xl font-semibold text-slate-900">{v}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="clay p-6 lg:col-span-2" data-testid="report-division-chart">
          <SectionHead title="Rapat per Divisi" sub="Jumlah rapat dan total menit terekam" />
          {d.by_division.length === 0 ? <Empty icon={BarChart3} title="Belum ada data divisi" sub="Buat rapat dengan metadata divisi." /> : (
            <div className="h-[300px] w-full min-w-0 -ml-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={d.by_division}>
                  <CartesianGrid strokeDasharray="3 6" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="division" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="meetings" name="Rapat" fill="#1D4ED8" radius={[8, 8, 0, 0]} barSize={26} />
                  <Bar dataKey="minutes" name="Menit" fill="#06B6D4" radius={[8, 8, 0, 0]} barSize={26} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="clay p-6" data-testid="report-completion-chart">
          <SectionHead title="Penyelesaian Action" sub="Distribusi status" />
          {completion.length === 0 ? <Empty icon={ListChecks} title="Belum ada action item" sub="Jalankan AI Summary pada rapat." /> : (
            <div className="h-[300px] w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={completion} dataKey="value" nameKey="name" innerRadius={62} outerRadius={98} paddingAngle={3}>
                    {completion.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="clay p-6" data-testid="audit-trail-card">
        <SectionHead title="Audit Trail" sub="Jejak aktivitas pengguna (25 terbaru)" />
        {logs.length === 0 ? <Empty icon={BarChart3} title="Audit trail kosong" sub="Aktivitas akan tercatat otomatis." /> : (
          <div className="space-y-1 max-h-[380px] overflow-y-auto pr-1">
            {logs.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-3 rounded-xl px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`audit-log-${l.id}`}>
                <span className="font-mono text-[11px] text-slate-400 w-[150px]">{new Date(l.created_at).toLocaleString("id-ID")}</span>
                <span className="rounded-full bg-brand-50 px-2.5 py-0.5 font-mono text-[11px] font-medium text-brand-700">{l.action}</span>
                <span className="text-sm text-slate-700">{l.user_name}</span>
                <span className="truncate text-xs text-slate-400">{l.target} {l.detail}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
